import '@assets/styles/components/_aid-modal.scss';
import { ReactElement, useCallback, useMemo } from 'react';
import * as Yup from 'yup';
import { Dropdown, TextInput } from '@components/common';
import { aidOptions, IAidModalConfig, IAidModalProps, IAidModalType } from '.';
import { useFormik } from 'formik';
import { useCAidMutation } from '@services/hooks/contractor360KAE/useCAidMutation';
import { ToastType, useTosterStore } from '@store/toster';
import useValidationFields from '@hooks/validations/fields';

export const AidModal = ({ type, userId, closeModal }: IAidModalProps): ReactElement => {
	const { setToaster } = useTosterStore();
	const { contractor_aid, no_of_carpenters } = useValidationFields();
	const aidModalConfig = useMemo<Record<IAidModalType, IAidModalConfig>>(
		() => ({
			aid: {
				fieldName: 'aid',
				label: 'AID',
				validation: contractor_aid,
				inputType: 'dropdown',
				options: aidOptions,
			},
			carpenter: {
				fieldName: 'carpenters_no',
				label: 'No of Carpenters',
				validation: no_of_carpenters,
				inputType: 'number',
			},
		}),
		[contractor_aid, no_of_carpenters]
	);

	const { mutate, isPending } = useCAidMutation();
	const config = aidModalConfig[type];

	const formik = useFormik({
		initialValues: {
			[config.fieldName]: '',
		},
		validationSchema: Yup.object({
			[config.fieldName]: config.validation,
		}),
		onSubmit: () => {
			handleSubmitForm();
		},
	});

	const isSubmitDisabled = useMemo(() => {
		const value = formik.values[config.fieldName];

		if (config.inputType === 'dropdown' && config.options) {
			return !config.options.some((opt) => opt.value === value);
		}

		if (config.inputType === 'number') {
			return !value || isNaN(Number(value));
		}

		return false;
	}, [formik.values, config]);

	const handleSubmitForm = useCallback(() => {
		const payload = {
			userId: userId,
			[config.fieldName]: formik.values[config.fieldName],
		};

		mutate(payload, {
			onSuccess: async (data) => {
				if (data) {
					closeModal();
					setToaster(true, ToastType.success, `${config.label} submitted successfully`);
				}
			},
			onError: (error: Error) => {
				closeModal();
				setToaster(true, ToastType.error, error.message);
			},
		});
	}, [formik.values, config, mutate, closeModal, setToaster, userId]);

	return (
		<div className="aid-modal-wrapper">
			<form onSubmit={formik.handleSubmit}>
				<div className="row">
					<div className="form-control d-col d-col-12">
						{config.inputType === 'dropdown' ? (
							<Dropdown
								id={config.fieldName}
								name={config.fieldName}
								label={config.label}
								title={formik.values[config.fieldName]}
								options={config.options ?? []}
								onBlur={formik.handleBlur}
								error={
									formik.touched[config.fieldName] && formik.errors[config.fieldName]
										? (formik.errors[config.fieldName] as string)
										: null
								}
								setFieldValue={formik.setFieldValue}
								disabled={isPending}
								required
							/>
						) : (
							<TextInput
								type="number"
								label={config.label}
								id={config.fieldName}
								name={config.fieldName}
								onBlur={formik.handleBlur}
								value={formik.values[config.fieldName]}
								error={
									formik.touched[config.fieldName] && formik.errors[config.fieldName]
										? formik.errors[config.fieldName]
										: null
								}
								setFieldValue={formik.setFieldValue}
								placeholder={config.label}
								disabled={isPending}
								required
							/>
						)}
					</div>
				</div>

				<div className="btn-wrapper">
					<button type="submit" aria-label="submit button" className="btn-primary btn" disabled={isSubmitDisabled}>
						{isPending ? 'Submitting...' : 'Submit'}
					</button>
				</div>
			</form>
		</div>
	);
};
